import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import {
  ReactiveFormsModule,
  FormGroup,
  FormBuilder,
  Validators,
} from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import {
  LoginInterface,
  LoginResponseInterface,
} from '@core/models/authentication.model';
import { AuthenticationService } from '@core/services/authentication/authentication.service';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { SESSION_KEYS } from '@feature/auth/constants/auth.constant';
import { InputModule } from '@sharedModule/components/input/input.module';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { REGEX_CONSTANT } from '@sharedModule/constants/pattern-regex.constant';
import { ROUTES } from '@sharedModule/constants/route.constants';
import { showValidationMsg } from '@sharedModule/utils/form.utils';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, RouterModule, ReactiveFormsModule, InputModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnInit, OnDestroy {
  public readonly attributeConstant = ATTRIBUTES_CONSTANT;
  public loginForm!: FormGroup;

  private readonly destroy$ = new Subject<void>();
  private readonly router = inject(Router);
  private readonly formBuilder = inject(FormBuilder);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly messageHandlerService = inject(MessageHandlerService);
  private readonly authenticationService = inject(AuthenticationService);

  ngOnInit(): void {
    this.initializeForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onSubmit(): void {
    if (!this.loginForm.valid) {
      showValidationMsg(this.loginForm);
      return;
    }

    const formData = this.loginForm.value as LoginInterface;
    this.performLogin(formData);
  }

  private initializeForm(): void {
    this.loginForm = this.formBuilder.group({
      [this.attributeConstant.email.key]: [
        '',
        [Validators.required, Validators.pattern(REGEX_CONSTANT.email)],
      ],
      [this.attributeConstant.password.key]: ['', [Validators.required]],
    });
  }

  private performLogin(formData: LoginInterface): void {
    this.authenticationService
      .login(formData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (!response.success) {
            this.messageHandlerService.show(response.message);
            return;
          }

          const isMfaDisabled = this.isMfaDisabled(response.response);

          if (isMfaDisabled) {
            this.handleDirectLogin(response.response);
          } else {
            this.handleMfaLogin(formData.email);
          }
        },
      });
  }

  private isMfaDisabled(response: LoginResponseInterface): boolean {
    return response?.is_mfa_disabled ?? false;
  }

  private handleDirectLogin(response: LoginResponseInterface): void {
    if (response) {
      this.authenticationService.onLoggedIn(response);
    }
  }

  private handleMfaLogin(email: string): void {
    this.sessionStorageService.set(SESSION_KEYS.USER_EMAIL, email);
    this.router.navigate([ROUTES.VERIFY_LOGIN_OTP]);
  }
}
